import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../store/store';
import { LoginResponse, UserResponse } from './types';
import { Logger } from '../../lib/utils';

export interface AuthState {
  token: string | null;
  user: UserResponse | null;
  tenantId: number | null;
  isAuthenticated: boolean;
  initialized: boolean;
}

const AUTH_TOKEN_KEY = 'authToken';

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function readStoredTenantId(): number | null {
  try {
    const raw = localStorage.getItem('tenantId');
    if (!raw || !/^\d+$/.test(raw)) return null;
    const parsed = Number.parseInt(raw, 10);
    return parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function resolveTenantId(user: UserResponse): number | null {
  if (user.empresaId) return user.empresaId;
  if (user.role === 'SUPERADMIN') return readStoredTenantId();
  return null;
}

const initialState: AuthState = {
  user: null,
  token: readStoredToken(),
  tenantId: null,
  isAuthenticated: false,
  initialized: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<LoginResponse>) => {
      const { token, data: user } = action.payload;
      if (!user) return;

      state.user = user;
      state.token = token ?? null;
      state.tenantId = resolveTenantId(user);
      state.isAuthenticated = true;
      state.initialized = true;

      if (token) {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
      } else {
        localStorage.removeItem(AUTH_TOKEN_KEY);
      }

      if (state.tenantId) {
        localStorage.setItem('tenantId', String(state.tenantId));
      }
    },
    setSession: (state, action: PayloadAction<{ user: UserResponse; tenantId?: number | null }>) => {
      state.user = action.payload.user;
      state.token = state.token ?? readStoredToken();
      state.tenantId =
        action.payload.tenantId ??
        resolveTenantId(action.payload.user);
      state.isAuthenticated = true;
      state.initialized = true;
    },
    setTenantId: (state, action: PayloadAction<number | null>) => {
      state.tenantId = action.payload;
      if (action.payload) {
        localStorage.setItem('tenantId', String(action.payload));
      } else {
        localStorage.removeItem('tenantId');
      }
    },
    logout: state => {
      state.token = null;
      state.user = null;
      state.tenantId = null;
      state.isAuthenticated = false;
      state.initialized = true;
      localStorage.removeItem('tenantId');
      localStorage.removeItem(AUTH_TOKEN_KEY);
    },
    initializeAuth: state => {
      state.initialized = false;
    },
    setInitialized: (state, action: PayloadAction<boolean>) => {
      state.initialized = action.payload;
    },
    setCurrentUser: (state, action: PayloadAction<UserResponse>) => {
      state.user = action.payload;
      state.tenantId = action.payload.empresaId ?? state.tenantId;
    },
  },
});

export const {
  setCredentials,
  setSession,
  setTenantId,
  logout,
  initializeAuth,
  setInitialized,
  setCurrentUser,
} = authSlice.actions;

export default authSlice.reducer;

export const selectCurrentUser = (state: RootState) => state.auth.user;
export const selectCurrentToken = (state: RootState) => state.auth.token;
export const selectTenantId = (state: RootState) => state.auth.tenantId;
export const selectIsAuthenticated = (state: RootState) => state.auth.isAuthenticated;
export const selectIsInitialized = (state: RootState) => state.auth.initialized;
